import LutsIcon from '@/components/Icons/Sidebar/Luts';
import MotionGraphicsIcon from '@/components/Icons/Sidebar/MotionGraphics';
import MusicIcon from '@/components/Icons/Sidebar/Music';
import SoundEffectsIcon from '@/components/Icons/Sidebar/SoundEffects';
import StockVideoIcon from '@/components/Icons/Sidebar/StockVideo';
import Tooltip from '@/components/Tooltip';
import { LICENSE_TOOLTIPS, PLAN_DESCRIPTIONS } from '@/lib/pricing/planCopy';
import { Info } from '@music-vine/cadence/icons';
import type { PlanBenefit } from './plans';
import type { ComponentType } from 'react';

/**
 * Card body content for the 2026 upsell/sign-up plan cards (DEV-6406).
 * One source for every upsell instance: library icon chips with tooltips,
 * the "License:" section, and the tick list, per plan.
 */

type PaidPlan = 'Essentials' | 'Creator' | 'Pro' | 'Business';
const LIBRARIES: {
  Icon: ComponentType<{
    className?: string;
  }>;
  label: string | ((plan: PaidPlan) => string);
  name: string;
}[] = [{
  name: 'music',
  Icon: MusicIcon,
  label: 'Music'
}, {
  name: 'sfx',
  Icon: SoundEffectsIcon,
  label: 'Sound effects'
}, {
  name: 'stock-video',
  Icon: StockVideoIcon,
  label: plan => plan === 'Creator' ? 'Stock video (up to 1080p)' : 'Stock video (up to 4K)'
}, {
  name: 'motion-graphics',
  Icon: MotionGraphicsIcon,
  label: 'Motion graphics'
}, {
  name: 'luts',
  Icon: LutsIcon,
  label: 'LUTs'
}];
const ALL_LIBRARIES = new Set(LIBRARIES.map(library => library.name));
const PLAN_LIBRARIES: Record<PaidPlan, Set<string>> = {
  Essentials: new Set(['music', 'sfx']),
  Creator: ALL_LIBRARIES,
  Pro: ALL_LIBRARIES,
  Business: ALL_LIBRARIES
};
const PlanLibraryIcons = ({
  plan
}: {
  plan: PaidPlan;
}) => <ul className="mt-4 mb-4 flex list-none gap-2 px-4 py-0" data-sentry-component="PlanLibraryIcons" data-sentry-source-file="planCardContent.tsx">
    {LIBRARIES.filter(library => PLAN_LIBRARIES[plan].has(library.name)).map(({
    name,
    Icon,
    label
  }) => {
    const resolvedLabel = typeof label === 'function' ? label(plan) : label;
    return <li key={name}>
            <Tooltip className="z-[9999]" label={resolvedLabel} showOnClick>
              {/* cursor-default: the chips are labels, and the card around
                  them is what's clickable (DEV-6614). */}
              <span className="flex size-8 cursor-default items-center justify-center rounded-lg bg-pink-50 text-pink-500 dark:bg-pink-900/40 dark:text-pink-300">
                <Icon className="size-4" />
                <span className="sr-only">{resolvedLabel}</span>
              </span>
            </Tooltip>
          </li>;
  })}
  </ul>;
const PLAN_LICENSE: Record<PaidPlan, {
  text: string;
  tooltip?: string;
}> = {
  Essentials: {
    text: 'Individuals. Covers your own content, but not content made for others'
  },
  Creator: {
    text: 'Individuals. Covers your own content, but not content made for others'
  },
  Pro: {
    text: 'Individuals, freelancers and solo professionals. Covers commercial digital use',
    tooltip: LICENSE_TOOLTIPS.proDigitalUse
  },
  Business: {
    text: 'Organizations, brands and teams. Covers commercial digital use',
    tooltip: LICENSE_TOOLTIPS.businessDigitalUse
  }
};
const PlanLicenseSection = ({
  plan
}: {
  plan: PaidPlan;
}) => {
  const license = PLAN_LICENSE[plan];

  // No bottom margin: StackingCardList's own pt-1.5 is the whole gap to the
  // first tick, which was double-spaced before (DEV-6610).
  return <div className="mx-4 border-0 border-gray-150 border-b border-solid pb-3 font-sans dark:border-gray-800" data-sentry-component="PlanLicenseSection" data-sentry-source-file="planCardContent.tsx">
      <p className="mb-1.5 font-bold text-gray-950 text-sm dark:text-white">
        License for:
      </p>
      <p className="text-gray-700 text-sm dark:text-gray-400">
        {license.text}
        {license.tooltip ? <Tooltip className="z-[9999]" label={license.tooltip} showOnClick side="bottom">
            <Info className="mb-1 ml-1 inline size-3 align-middle" />
          </Tooltip> : null}
      </p>
    </div>;
};
const PLAN_TICK_LIST: Record<PaidPlan, string[]> = {
  Essentials: ['Unlimited access to music & sound effects', 'Safelist 1 YouTube channel'],
  Creator: ['Unlimited access across all libraries', 'Safelist 3 YouTube channels', '1080p footage'],
  Pro: ['Unlimited access across all libraries', 'Safelist 10 YouTube channels', 'WAV audio + 4K footage'],
  Business: ['Unlimited access across all libraries', 'Safelist 10 YouTube channels', 'WAV audio + 4K footage']
};
export interface PlanCardContent {
  benefits: PlanBenefit[];
  description: string;
  preList: React.ReactNode;
}

/** The refreshed card body for a paid plan (2026 designs). */
export const getPlanCardContent = (plan: PaidPlan): PlanCardContent => ({
  description: PLAN_DESCRIPTIONS[plan],
  preList: <>
      <PlanLibraryIcons plan={plan} />
      <PlanLicenseSection plan={plan} />
    </>,
  benefits: PLAN_TICK_LIST[plan].map((text, index) => ({
    id: index + 1,
    text
  }))
});